import type { CreateAgendaSlotItemBulkErrorData } from '@omecare/utils';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { pushSheet } from '@/components/sheets';
import { getOfficeAgendaSlotById } from '@/hooks/queries/use-get-agenda-slot-by-id';
import { getOfficeWorkScheduleSlotById } from '@/hooks/queries/use-get-office-work-schedule-slot-by-id';

const openWorkScheduleSlotSheet = async ({
  officeId,
  slotId,
  queryClient,
}: {
  officeId: string;
  slotId: string;
  queryClient: QueryClient;
}) => {
  const slotInformation = await getOfficeWorkScheduleSlotById(slotId)();

  pushSheet('ViewAdminWorkScheduleSlotSheet', {
    officeId,
    slot: slotInformation,
    eventOccurrence: {
      id: null,
      start: new Date(slotInformation.startAt),
      end: new Date(slotInformation.endAt),
      allday: slotInformation.periodOfTheDay === 'allday',
      index: 0,
    },
    onAfterDelete: () => {
      queryClient.invalidateQueries({
        queryKey: ['office-work-schedule-slots'],
      });
    },
    onAfterUpdated: () => {
      queryClient.invalidateQueries({
        queryKey: ['office-work-schedule-slots'],
      });
    },
  });
};

const openAgendaSlotSheet = async ({
  officeId,
  slotId,
  queryClient,
}: {
  officeId: string;
  slotId: string;
  queryClient: QueryClient;
}) => {
  const slotInformation = await getOfficeAgendaSlotById(slotId)();

  pushSheet('ViewAdminAgendaSlotSheet', {
    officeId,
    slot: slotInformation,
    eventOccurrence: {
      id: null,
      start: new Date(slotInformation.startAt),
      end: new Date(slotInformation.endAt),
      allday: slotInformation.periodOfTheDay === 'allday',
      index: 0,
    },
    onAfterDelete: () => {
      queryClient.invalidateQueries({
        queryKey: ['office-agenda-slots'],
      });
    },
    onAfterBooked: () => {
      queryClient.invalidateQueries({
        queryKey: ['office-agenda-slots'],
      });
    },
    onAfterUpdated: () => {
      queryClient.invalidateQueries({
        queryKey: ['office-agenda-slots'],
      });
    },
    onAfterUpdateReservation: () => {
      queryClient.invalidateQueries({
        queryKey: ['office-agenda-slots'],
      });
    },
  });
};

export const useCreateAgendaSlotBulkListReportViewModel = ({
  officeId,
}: {
  officeId: string;
}) => {
  const queryClient = useQueryClient();

  return {
    openSlotModal: async (slot: CreateAgendaSlotItemBulkErrorData) => {
      if (!slot.slotIntersected) {
        return;
      }

      if (slot.slotIntersected.entityType === 'agenda-slot') {
        return openAgendaSlotSheet({
          officeId,
          slotId: slot.slotIntersected.id,
          queryClient,
        });
      }

      if (slot.slotIntersected.entityType === 'work-schedule') {
        return openWorkScheduleSlotSheet({
          officeId,
          slotId: slot.slotIntersected.id,
          queryClient,
        });
      }
    },
  };
};
